import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import {
    X,
    ChevronRight,
    ChevronLeft,
    Save,
    Send,
    Info,
    Building2,
    MapPin,
    Briefcase,
    DollarSign,
    FlaskConical,
    GraduationCap,
    Phone
} from 'lucide-react';
import doctoralFields from '../../data/doctoral_fields.json';
import institutionOptions from '../../data/institution_options.json';
import styles from './ProfessorPositions.module.css';

const fieldOptions = doctoralFields.map(({ code, field }) => ({
    value: field,
    label: `${field} (${code})`,
}));

const semesterOptions = [
    { value: 'Fall', label: 'Fall' },
    { value: 'Spring', label: 'Spring' },
    { value: 'Summer', label: 'Summer' },
];

const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = new Date().getFullYear() + i;
    return { value: year, label: `${year}` };
});

const fundingStatusOptions = [
    { value: 'Fully Funded', label: 'Fully Funded' },
    { value: 'Partially Funded', label: 'Partially Funded' },
    { value: 'Not Funded', label: 'Not Funded' },
];

const fundingSourceOptions = [
    { value: 'Grant', label: 'Grant' },
    { value: 'Teaching Assistant', label: 'Teaching Assistant' },
    { value: 'Research Assistant', label: 'Research Assistant' },
    { value: 'Fellowship', label: 'Fellowship' },
    { value: 'Other', label: 'Other' },
];

const labSizeOptions = [
    { value: 'Small (1-10 people)', label: 'Small (1-10 people)' },
    { value: 'Medium (10-15 people)', label: 'Medium (10-15 people)' },
    { value: 'Large (15+ people)', label: 'Large (15+ people)' },
];

const publicationRateOptions = [
    { value: 'Low (0-2 papers/year)', label: 'Low (0-2 papers/year)' },
    { value: 'Medium (2-5 papers/year)', label: 'Medium (2-5 papers/year)' },
    { value: 'High (5+ papers/year)', label: 'High (5+ papers/year)' },
];

const cityTypeOptions = [
    { value: 'Big city', label: 'Big city' },
    { value: 'Urban', label: 'Urban' },
    { value: 'Suburban', label: 'Suburban' },
    { value: 'Rural', label: 'Rural' },
    { value: 'College Town', label: 'College Town' },
];

const stepLabels = [
    'Basic Info',
    'Description',
    'Funding',
    'Requirements'
];

export default function CreatePositionModal({ isOpen, onClose, onPositionCreated, userData, initialData }) {
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        fields: [],
        institution: '',
        city: '',
        country: '',
        link: '',
        start_semester: '',
        start_year: '',
        native: true,
        financial_info: {
            fully_funded: 'Not Funded',
            funding_source: '',
            expected_graduation_time: '',
        },
        lab_environment: {
            size: '',
            equipment_resources: '',
            collaboration: '',
            publication_rate: '',
        },
        location_details: {
            expected_cost_of_living: '',
            city_type: '',
        },
        minimum_requirements: {
            degree_level: '',
            degree_field: '',
            gpa: '',
            gre: '',
            toefl: '',
            research_experience: '',
            other_requirements: '',
        },
        contact_info: {
            email: '',
            phone: '',
            website: '',
        },
        customFields: ['', ''],
    });

    // Sync with initialData if editing
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                const preservedFields = initialData.fields || [];
                const standardFields = fieldOptions.map(o => o.value);
                const customOnes = preservedFields.filter(f => !standardFields.includes(f));
                const filteredStandard = preservedFields.filter(f => standardFields.includes(f));

                setFormData({
                    ...initialData,
                    fields: customOnes.length > 0 ? [...filteredStandard, 'other'] : filteredStandard,
                    financial_info: initialData.financial_info || {},
                    lab_environment: initialData.lab_environment || {},
                    location_details: initialData.location_details || {},
                    minimum_requirements: initialData.minimum_requirements || {},
                    contact_info: initialData.contact_info || {},
                    customFields: [customOnes[0] || '', customOnes[1] || ''],
                });
            } else {
                setFormData({
                    title: '',
                    description: '',
                    fields: [],
                    institution: '',
                    city: '',
                    country: '',
                    link: '',
                    start_semester: '',
                    start_year: '',
                    native: true,
                    financial_info: {
                        fully_funded: 'Not Funded',
                        funding_source: '',
                        expected_graduation_time: '',
                    },
                    lab_environment: {
                        size: '',
                        equipment_resources: '',
                        collaboration: '',
                        publication_rate: '',
                    },
                    location_details: {
                        expected_cost_of_living: '',
                        city_type: '',
                    },
                    minimum_requirements: {
                        degree_level: '',
                        degree_field: '',
                        gpa: '',
                        gre: '',
                        toefl: '',
                        research_experience: '',
                        other_requirements: '',
                    },
                    contact_info: {
                        email: '',
                        phone: '',
                        website: '',
                    },
                    customFields: ['', ''],
                });
            }
            setStep(1);
        }
    }, [isOpen, initialData]);

    // Handle outside click to close
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleCloseWithPrompt = () => {
        if (isSubmitting) {
            onClose();
            return;
        }

        const isLive = formData.status === 'active';

        if (isLive) {
            const shouldUpdate = window.confirm('You are editing a LIVE position. Would you like to save your changes before closing?');
            if (shouldUpdate) {
                handleSubmit('active');
                return;
            }
        } else if (formData.title || formData.description) {
            const saveDraft = window.confirm('Would you like to save this as a draft before closing?');
            if (saveDraft) {
                handleSubmit('draft');
                return;
            }
        }

        onClose();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSelectChange = (name, selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleMultiSelectChange = (name, selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        setFormData(prev => ({ ...prev, [name]: values }));
    };

    const handleSubmit = async (status = 'active') => {
        if (status === 'active' && (!formData.title || !formData.description)) {
            alert('Please fill in required fields (Title and Description)');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = { ...formData, status };

            // Clean up empty strings to undefined or specific types
            if (payload.location_details.expected_cost_of_living === '') delete payload.location_details.expected_cost_of_living;
            if (payload.minimum_requirements.gpa === '') delete payload.minimum_requirements.gpa;
            if (payload.minimum_requirements.gre === '') delete payload.minimum_requirements.gre;
            if (payload.minimum_requirements.toefl === '') delete payload.minimum_requirements.toefl;

            // Strip empty strings for ENUM and Number fields to allow Mongoose defaults or avoid validation errors
            if (payload.start_semester === '') delete payload.start_semester;
            if (payload.start_year === '') delete payload.start_year;

            if (payload.financial_info) {
                if (payload.financial_info.fully_funded === '') delete payload.financial_info.fully_funded;
                if (payload.financial_info.funding_source === '') delete payload.financial_info.funding_source;
            }
            if (payload.lab_environment) {
                if (payload.lab_environment.size === '') delete payload.lab_environment.size;
                if (payload.lab_environment.publication_rate === '') delete payload.lab_environment.publication_rate;
            }
            if (payload.location_details) {
                if (payload.location_details.city_type === '') delete payload.location_details.city_type;
            }

            // Clean up empty nested objects if all their fields are gone
            const categories = ['financial_info', 'lab_environment', 'location_details', 'minimum_requirements', 'contact_info'];
            categories.forEach(cat => {
                if (payload[cat] && Object.keys(payload[cat]).length === 0) {
                    delete payload[cat];
                }
            });

            // Handle "Other" research fields
            if (payload.fields && payload.fields.includes('other')) {
                const combinedFields = [
                    ...payload.fields.filter(f => f !== 'other'),
                    ...payload.customFields.filter(f => f && f.trim() !== '')
                ];
                payload.fields = combinedFields.slice(0, 3); // Limit to a reasonable number
            }
            delete payload.customFields;

            const isEdit = !!payload._id;
            const url = isEdit
                ? `${process.env.REACT_APP_BACKEND_URL}/api/positions/${payload._id}`
                : `${process.env.REACT_APP_BACKEND_URL}/api/positions`;

            const method = isEdit ? 'put' : 'post';

            await axios[method](url, payload, {
                headers: {
                    Authorization: `Bearer ${userData.token}`,
                    'Content-Type': 'application/json'
                },
            });

            alert(`✅ Position successfully ${status === 'draft' ? 'saved as draft' : (isEdit ? 'updated' : 'published')}!`);
            onPositionCreated();
            onClose();
        } catch (error) {
            console.error('Error creating position:', error);
            alert('❌ Failed to save position');
        } finally {
            setIsSubmitting(false);
        }
    };

    const nextStep = () => setStep(prev => Math.min(prev + 1, 4));
    const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: "#F4F3EE",
            borderColor: "#3C6E71",
            padding: "2px",
            minHeight: "45px",
        })
    };

    const StepIndicator = () => (
        <div className={styles.stepIndicator}>
            {[1, 2, 3, 4].map((i) => (
                <React.Fragment key={i}>
                    <div className={styles.stepWrapper}>
                        <div className={`${styles.stepCircle} ${step >= i ? styles.stepActive : ''}`}>
                            {i}
                        </div>
                        <span className={`${styles.stepLabel} ${step >= i ? styles.stepLabelActive : ''}`}>
                            {stepLabels[i - 1]}
                        </span>
                    </div>
                    {i < 4 && <div className={`${styles.stepLine} ${step > i ? styles.stepLineActive : ''}`} />}
                </React.Fragment>
            ))}
        </div>
    );

    return (
        <div className={styles.modalOverlay} onClick={handleCloseWithPrompt}>
            <div className={styles.modalContainerLarge} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.enhancedModalHeader}>
                    <div className={styles.modalHeaderTop}>
                        <div className={styles.modalHeaderTitle}>
                            <h2 className={styles.modalTitle}>{initialData ? 'Edit Position' : 'Create New Position'}</h2>
                            <p className={styles.modalSubtitle}>Fill in the details to find your next researcher</p>
                        </div>
                        <button className={styles.modalCloseBtn} onClick={handleCloseWithPrompt}><X size={24} /></button>
                    </div>
                    <StepIndicator />
                </div>

                {/* Form Body */}
                <div className={styles.enhancedModalBody}>
                    <div className={styles.formContentWrapper}>
                        {step === 1 && (
                            <div className={styles.stepContainer}>
                                <div className={styles.stepHeader}>
                                    <Building2 className={styles.stepIcon} />
                                    <h3>Basic Information</h3>
                                </div>
                                <div className={styles.inputGrid}>
                                    <div className={styles.inputGroupFull}>
                                        <label>Position Title *</label>
                                        <input
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            placeholder="e.g. PhD Position in AI Ethics"
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Institution</label>
                                        <Select
                                            options={institutionOptions}
                                            value={institutionOptions.find(o => o.value === formData.institution)}
                                            onChange={(opt) => handleSelectChange('institution', opt)}
                                            styles={customSelectStyles}
                                            isSearchable
                                            placeholder="Select university..."
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Additional Info Link</label>
                                        <input
                                            name="link"
                                            value={formData.link}
                                            onChange={handleInputChange}
                                            placeholder="https://lab-website.com"
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>City</label>
                                        <input
                                            name="city"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            placeholder="e.g. Cambridge"
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Country</label>
                                        <input
                                            name="country"
                                            value={formData.country}
                                            onChange={handleInputChange}
                                            placeholder="e.g. United Kingdom"
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Start Semester</label>
                                        <Select
                                            options={semesterOptions}
                                            value={semesterOptions.find(o => o.value === formData.start_semester)}
                                            onChange={(opt) => handleSelectChange('start_semester', opt)}
                                            styles={customSelectStyles}
                                            placeholder="Fall, Spring..."
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Start Year</label>
                                        <Select
                                            options={yearOptions}
                                            value={yearOptions.find(o => o.value === formData.start_year)}
                                            onChange={(opt) => handleSelectChange('start_year', opt)}
                                            styles={customSelectStyles}
                                            placeholder="2025..."
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className={styles.stepContainer}>
                                <div className={styles.stepHeader}>
                                    <Briefcase className={styles.stepIcon} />
                                    <h3>Description & Fields</h3>
                                </div>
                                <div className={styles.inputGrid}>
                                    <div className={styles.inputGroupFull}>
                                        <label>Research Fields (Max 2) *</label>
                                        <Select
                                            isMulti
                                            options={[...fieldOptions, { value: 'other', label: 'Other' }]}
                                            value={[
                                                ...fieldOptions.filter(o => formData.fields.includes(o.value)),
                                                ...(formData.fields.includes('other') ? [{ value: 'other', label: 'Other' }] : [])
                                            ]}
                                            onChange={(opts) => {
                                                const values = opts ? opts.map(opt => opt.value).slice(0, 2) : [];
                                                setFormData(prev => ({ ...prev, fields: values }));
                                            }}
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                    {formData.fields.includes('other') && (
                                        <div className={styles.inputGroupFull}>
                                            <div className={styles.locationGrid}>
                                                <input
                                                    placeholder="Custom Field 1"
                                                    value={formData.customFields[0]}
                                                    onChange={(e) => {
                                                        const newCustom = [...formData.customFields];
                                                        newCustom[0] = e.target.value;
                                                        setFormData(prev => ({ ...prev, customFields: newCustom }));
                                                    }}
                                                />
                                                <input
                                                    placeholder="Custom Field 2"
                                                    value={formData.customFields[1]}
                                                    onChange={(e) => {
                                                        const newCustom = [...formData.customFields];
                                                        newCustom[1] = e.target.value;
                                                        setFormData(prev => ({ ...prev, customFields: newCustom }));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className={styles.inputGroupFull}>
                                        <label>Position Description *</label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows={8}
                                            placeholder="Detailed overview of the role..."
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Expected Cost of Living ($/mo)</label>
                                        <input
                                            type="number"
                                            name="location_details.expected_cost_of_living"
                                            value={formData.location_details.expected_cost_of_living}
                                            onChange={handleInputChange}
                                            placeholder="e.g. 1500"
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>City Type</label>
                                        <Select
                                            options={cityTypeOptions}
                                            value={cityTypeOptions.find(o => o.value === formData.location_details.city_type)}
                                            onChange={(opt) => handleSelectChange('location_details.city_type', opt)}
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className={styles.stepContainer}>
                                <div className={styles.stepHeader}>
                                    <DollarSign className={styles.stepIcon} />
                                    <h3>Funding & Environment</h3>
                                </div>
                                <div className={styles.inputGrid}>
                                    <div className={styles.inputGroup}>
                                        <label>Funding Status</label>
                                        <Select
                                            options={fundingStatusOptions}
                                            value={fundingStatusOptions.find(o => o.value === formData.financial_info.fully_funded)}
                                            onChange={(opt) => handleSelectChange('financial_info.fully_funded', opt)}
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Funding Source</label>
                                        <Select
                                            options={fundingSourceOptions}
                                            value={fundingSourceOptions.find(o => o.value === formData.financial_info.funding_source)}
                                            onChange={(opt) => handleSelectChange('financial_info.funding_source', opt)}
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                    <div className={styles.inputGroupFull}>
                                        <label>Expected Duration (e.g. 4-5 years)</label>
                                        <input
                                            name="financial_info.expected_graduation_time"
                                            value={formData.financial_info.expected_graduation_time}
                                            onChange={handleInputChange}
                                            placeholder="Duration of program"
                                        />
                                    </div>

                                    <div className={styles.stepHeaderSmall}>
                                        <FlaskConical size={20} />
                                        <h4>Lab Environment</h4>
                                    </div>

                                    <div className={styles.inputGroup}>
                                        <label>Lab Size</label>
                                        <Select
                                            options={labSizeOptions}
                                            value={labSizeOptions.find(o => o.value === formData.lab_environment.size)}
                                            onChange={(opt) => handleSelectChange('lab_environment.size', opt)}
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Publication Rate</label>
                                        <Select
                                            options={publicationRateOptions}
                                            value={publicationRateOptions.find(o => o.value === formData.lab_environment.publication_rate)}
                                            onChange={(opt) => handleSelectChange('lab_environment.publication_rate', opt)}
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                    <div className={styles.inputGroupFull}>
                                        <label>Equipment & Resources</label>
                                        <textarea
                                            name="lab_environment.equipment_resources"
                                            value={formData.lab_environment.equipment_resources}
                                            onChange={handleInputChange}
                                            rows={2}
                                            placeholder="Available facilities..."
                                        />
                                    </div>
                                    <div className={styles.inputGroupFull}>
                                        <label>Collaboration Spirit</label>
                                        <textarea
                                            name="lab_environment.collaboration"
                                            value={formData.lab_environment.collaboration}
                                            onChange={handleInputChange}
                                            rows={2}
                                            placeholder="How lab members work together..."
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 4 && (
                            <div className={styles.stepContainer}>
                                <div className={styles.stepHeader}>
                                    <GraduationCap className={styles.stepIcon} />
                                    <h3>Requirements & Contact</h3>
                                </div>
                                <div className={styles.inputGrid}>
                                    <div className={styles.inputGroup}>
                                        <label>Min Degree Level</label>
                                        <input
                                            name="minimum_requirements.degree_level"
                                            value={formData.minimum_requirements.degree_level}
                                            onChange={handleInputChange}
                                            placeholder="e.g. MSc or BSc"
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Min GPA</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            name="minimum_requirements.gpa"
                                            value={formData.minimum_requirements.gpa}
                                            onChange={handleInputChange}
                                            placeholder="3.5"
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Min GRE</label>
                                        <input
                                            type="number"
                                            name="minimum_requirements.gre"
                                            value={formData.minimum_requirements.gre}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Min TOEFL/IELTS</label>
                                        <input
                                            type="number"
                                            name="minimum_requirements.toefl"
                                            value={formData.minimum_requirements.toefl}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className={styles.inputGroupFull}>
                                        <label>Research Experience Required</label>
                                        <input
                                            name="minimum_requirements.research_experience"
                                            value={formData.minimum_requirements.research_experience}
                                            onChange={handleInputChange}
                                            placeholder="Description of required experience"
                                        />
                                    </div>

                                    <div className={styles.stepHeaderSmall}>
                                        <Phone size={20} />
                                        <h4>Contact Info</h4>
                                    </div>

                                    <div className={styles.inputGroup}>
                                        <label>Contact Email</label>
                                        <input
                                            name="contact_info.email"
                                            value={formData.contact_info.email}
                                            onChange={handleInputChange}
                                            placeholder="lab@university.edu"
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Lab/Position Website</label>
                                        <input
                                            name="contact_info.website"
                                            value={formData.contact_info.website}
                                            onChange={handleInputChange}
                                            placeholder="https://lab.edu"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className={styles.enhancedModalFooter}>
                    <div className={styles.leftActions}>
                        <button
                            className={styles.modalSecondaryBtn}
                            onClick={() => handleSubmit('draft')}
                            disabled={isSubmitting}
                        >
                            <Save size={18} />
                            Save draft
                        </button>
                    </div>

                    <div className={styles.rightActions}>
                        {step > 1 && (
                            <button
                                className={styles.modalNavigationBtn}
                                onClick={prevStep}
                            >
                                <ChevronLeft size={20} />
                                Back
                            </button>
                        )}

                        {step < 4 ? (
                            <button
                                className={styles.modalNavigationBtnActive}
                                onClick={nextStep}
                            >
                                Next
                                <ChevronRight size={20} />
                            </button>
                        ) : (
                            <button
                                className={styles.modalSubmitBtn}
                                onClick={() => handleSubmit('active')}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (initialData ? 'Updating...' : 'Posting...') : (initialData ? 'Update Position' : 'Post Position')}
                                <Send size={18} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
